import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  LockClosedIcon,
  GlobeAltIcon,
  ClockIcon,
  ArrowPathIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { makeApiRequest } from '../utils/api';
import config from '../config';

// Simple donut chart component
function DonutChart({ segments, size = 120, strokeWidth = 10 }) {
  const center = size / 2;
  const radius = size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  
  let startAngle = 0;
  
  // Calculate total for percentages
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
      {total > 0 ? (
        segments.map((segment, i) => {
          const percentage = segment.value / total;
          const dashArray = circumference * percentage;
          const dashOffset = circumference * (1 - percentage);
          const angle = startAngle;
          startAngle += percentage * 360;
          
          return (
            <circle
              key={i}
              cx={center}
              cy={center}
              r={radius}
              fill="transparent"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dashArray} ${circumference - dashArray}`}
              strokeDashoffset={-circumference * angle / 360}
              strokeLinecap="round"
            />
          );
        })
      ) : (
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke="#374151"
          strokeWidth={strokeWidth}
        />
      )}
      <circle cx={center} cy={center} r={radius - strokeWidth * 1.5} fill="#111827" />
    </svg>
  );
}

// Stats card component
function StatCard({ icon: Icon, title, value, color, percent, change, tooltip }) {
  return (
    <div className="bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-700 relative group">
      <div className="absolute -inset-1 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 rounded-xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
      <div className="relative flex items-start">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="h-7 w-7 text-white" />
        </div>
        <div className="ml-4">
          <h3 className="text-gray-400 text-sm font-medium">{title}</h3>
          <div className="flex items-baseline">
            <p className="text-2xl font-bold text-white mt-1">{value}</p>
            {percent && (
              <span className={`ml-2 text-sm font-medium ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {change >= 0 ? '↑' : '↓'} {Math.abs(change)}%
              </span>
            )}
          </div>
          {tooltip && <p className="text-xs text-gray-500 mt-1">{tooltip}</p>}
        </div>
      </div>
    </div>
  );
}

function Admin() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const fetchStats = async (showRefreshing = false) => {
    if (showRefreshing) setIsRefreshing(true);
    
    try {
      if (!showRefreshing) setLoading(true);
      
      console.log('Fetching admin stats from:', config.apiUrl);
      
      // For debugging, log the full API URL
      const apiUrl = `${config.apiUrl}/api/rooms/stats`;
      console.log('Stats endpoint URL:', apiUrl);
      
      // First try without credentials as server auth is now disabled
      const data = await makeApiRequest('api/rooms/stats');
      console.log('Stats data received:', data);
      
      if (data && data.data) {
        setStats(data.data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        console.error('Invalid stats data format:', data);
        setError('Received invalid data format from server');
        toast.error('Received invalid data from server');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err.message || 'Failed to load statistics');
      toast.error(`Failed to load statistics: ${err.message}`);
    } finally {
      setLoading(false);
      if (showRefreshing) setIsRefreshing(false);
    }
  };
  
  useEffect(() => {
    fetchStats();
    
    // Set up an interval to refresh stats periodically
    const interval = setInterval(() => fetchStats(true), 60000); // Refresh every minute
    
    return () => clearInterval(interval);
  }, []);
  
  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };
  
  // Calculate room distribution for the donut chart
  const roomDistribution = useMemo(() => {
    if (!stats) return [];
    
    return [
      { name: 'Public Rooms', value: stats.rooms.public, color: '#10B981' }, // green-500
      { name: 'Private Rooms', value: stats.rooms.private, color: '#8B5CF6' } // purple-500
    ];
  }, [stats]);
  
  // Calculate some additional metrics
  const metrics = useMemo(() => {
    if (!stats) return {};
    
    const publicRatio = stats.rooms.total > 0
      ? Math.round((stats.rooms.public / stats.rooms.total) * 100)
      : 0;
      
    const privateRatio = stats.rooms.total > 0
      ? Math.round((stats.rooms.private / stats.rooms.total) * 100)
      : 0;
      
    const avgUsersPerRoom = stats.rooms.total > 0
      ? (stats.users.active / stats.rooms.total).toFixed(1)
      : '0';
      
    const avgMessagesPerRoom = stats.rooms.total > 0
      ? Math.round(stats.messages.total / stats.rooms.total)
      : 0;
      
    return {
      publicRatio,
      privateRatio,
      avgUsersPerRoom,
      avgMessagesPerRoom
    };
  }, [stats]);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Background patterns */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMTI5MzkiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzBoLTZWMGg2djMwem0tNiAwaC02djE1aDZ2LTE1eiIvPjwvZz48L2c+PC9zdmc+')] opacity-5"></div>
      
      <div className="container mx-auto py-8 px-4 relative">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Link to="/join-room" className="text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1">
                <ArrowLeftIcon className="h-4 w-4" />
                <span>Back</span>
              </Link>
            </div>
            <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">
              Chat Statistics Dashboard
            </h1>
            <p className="text-gray-400 mt-1">Real-time metrics for your chat application</p>
          </div>
          
          <button 
            onClick={() => fetchStats(true)}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:opacity-70 rounded-lg transition-colors"
          >
            {isRefreshing ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Refreshing...</span>
              </>
            ) : (
              <>
                <ArrowPathIcon className="h-4 w-4" />
                <span>Refresh Now</span>
              </>
            )}
          </button>
        </div>
        
        {loading && !stats && (
          <div className="flex items-center justify-center h-64">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <ChartBarIcon className="h-8 w-8 text-blue-500" />
              </div>
            </div>
          </div>
        )}
        
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-xl p-6 mb-6 max-w-lg mx-auto">
            <h3 className="text-red-400 text-lg font-medium mb-2">Unable to load statistics</h3>
            <p className="text-red-300">{error}</p>
            <button 
              onClick={() => fetchStats()}
              className="mt-4 px-4 py-2 bg-red-800 hover:bg-red-700 rounded-lg transition-colors text-sm"
            >
              Try Again
            </button>
          </div>
        )}
        
        {stats && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard 
                icon={GlobeAltIcon} 
                title="Public Rooms" 
                value={formatNumber(stats.rooms.public)}
                color="bg-gradient-to-br from-green-600 to-green-700"
                tooltip={`${metrics.publicRatio}% of all rooms`}
              />
              <StatCard 
                icon={LockClosedIcon} 
                title="Private Rooms" 
                value={formatNumber(stats.rooms.private)}
                color="bg-gradient-to-br from-purple-600 to-purple-700"
                tooltip={`${metrics.privateRatio}% of all rooms`}
              />
              <StatCard 
                icon={UserGroupIcon} 
                title="Active Users" 
                value={formatNumber(stats.users.active)}
                color="bg-gradient-to-br from-yellow-600 to-yellow-700"
                tooltip={`${metrics.avgUsersPerRoom} users per room (avg)`}
              />
              <StatCard 
                icon={ChatBubbleLeftRightIcon} 
                title="Total Messages" 
                value={formatNumber(stats.messages.total)}
                color="bg-gradient-to-br from-blue-600 to-blue-700"
                tooltip={`${formatNumber(metrics.avgMessagesPerRoom)} per room (avg)`}
              />
            </div>
            
            {/* Detailed Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Room Distribution */}
              <div className="bg-gray-800/80 backdrop-blur-md rounded-xl p-6 shadow-lg border border-gray-700">
                <h2 className="text-xl font-semibold mb-5 text-white flex items-center gap-2">
                  <span className="p-1.5 bg-blue-500/20 rounded-md">
                    <ChartBarIcon className="h-5 w-5 text-blue-400" />
                  </span>
                  Room Distribution
                </h2>
                
                <div className="flex flex-col items-center justify-center p-4">
                  <div className="relative">
                    <DonutChart segments={roomDistribution} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <span className="text-2xl font-bold text-white">{stats.rooms.total}</span>
                        <span className="block text-xs text-gray-400">Total Rooms</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-6 w-full">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-sm text-gray-300">Public ({metrics.publicRatio}%)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-purple-500 mr-2"></div>
                      <span className="text-sm text-gray-300">Private ({metrics.privateRatio}%)</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* User Activity */}
              <div className="bg-gray-800/80 backdrop-blur-md rounded-xl p-6 shadow-lg border border-gray-700">
                <h2 className="text-xl font-semibold mb-5 text-white flex items-center gap-2">
                  <span className="p-1.5 bg-yellow-500/20 rounded-md">
                    <UserGroupIcon className="h-5 w-5 text-yellow-400" />
                  </span>
                  User Activity
                </h2>
                
                <div className="space-y-4">
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Active Users</span>
                      <span className="text-xl font-semibold text-white">{formatNumber(stats.users.active)}</span>
                    </div>
                    <div className="mt-2">
                      <div className="h-2 w-full bg-gray-600 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full" 
                          style={{ width: `${Math.min(stats.users.active * 5, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Average per Room</span>
                      <span className="text-xl font-semibold text-white">{metrics.avgUsersPerRoom}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Users per chat room</p>
                  </div>
                </div>
              </div>
              
              {/* Message Metrics */}
              <div className="bg-gray-800/80 backdrop-blur-md rounded-xl p-6 shadow-lg border border-gray-700">
                <h2 className="text-xl font-semibold mb-5 text-white flex items-center gap-2">
                  <span className="p-1.5 bg-blue-500/20 rounded-md">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-400" />
                  </span>
                  Message Metrics
                </h2>
                
                <div className="space-y-4">
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Total Messages</span>
                      <span className="text-xl font-semibold text-white">{formatNumber(stats.messages.total)}</span>
                    </div>
                  </div>
                  
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Average per Room</span>
                      <span className="text-xl font-semibold text-white">{formatNumber(metrics.avgMessagesPerRoom)}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Messages per chat room</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Last Updated */}
            <div className="text-center mt-8 text-gray-400 flex flex-col sm:flex-row items-center justify-center gap-2">
              <ClockIcon className="h-4 w-4" />
              <p>Statistics last updated: {lastUpdated.toLocaleTimeString()}</p>
              <span className="hidden sm:inline mx-2">•</span>
              <p>Auto-refreshes every minute</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Admin; 